import { supabase } from '@/lib/supabase';
import { generateAPIUrl } from '@/utils';

export type RequestDeletionResult =
  | { success: true; expiresInMinutes: number }
  | { success: false; error: string };

/**
 * AccountDeletionManager — initiates the email-confirmation deletion flow.
 *
 * No longer performs the deletion itself. The user receives a link by email
 * and finalizes the delete on /confirm-delete (which calls
 * /api/account/delete-confirm). That decoupling is what lets us avoid the
 * heavy in-app re-auth modal.
 */
export class AccountDeletionManager {
  async requestDeletion(): Promise<RequestDeletionResult> {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      return { success: false, error: 'Not signed in' };
    }

    let response: Response;
    try {
      response = await fetch(generateAPIUrl('/api/account/delete'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Network error',
      };
    }

    if (!response.ok) {
      let message = 'Failed to send confirmation email';
      try {
        const payload = (await response.json()) as { error?: string };
        if (payload.error) message = payload.error;
      } catch {
        // ignore parse failure
      }
      return { success: false, error: message };
    }

    let payload: { expiresInMinutes?: number } = {};
    try {
      payload = (await response.json()) as { expiresInMinutes?: number };
    } catch {
      // server should always return JSON, but tolerate missing body
    }

    return {
      success: true,
      expiresInMinutes: payload.expiresInMinutes ?? 30,
    };
  }
}

export const accountDeletionManager = new AccountDeletionManager();
